import React, { useEffect, useMemo, useState } from 'react';

import {
  ButtonComponentActionConfigVariant,
  DropdownOption,
  Schedule,
  Sequence,
  SettingAction,
} from '@repo/sdk/models/components';

import { AlertDialogTitleHeader } from '@/components/molecules/alert-dialog-title-header';
import { TooltipButton } from '@/components/molecules/tooltip-button';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { keepEscapeInDialog } from '@/utils/keep-escape-in-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogFooterButtons,
  AlertDialogTrigger,
} from '@/ux-common/components/alert-dialog/alert-dialog';
import { Button } from '@/ux-common/components/button/button';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';
import { cn } from '@/ux-common/lib/utils';

import { ActionIcon } from './action-icon';
import { ConfirmationDescription } from './confirmation-description';
import { ActionComponentProps } from './types';
import { useHandleUpdateSettings } from './use-handle-update-settings';
import { mapLegacyButtonVariantToClassName } from '../utils';

type SettingValueKind = string | number | boolean | Schedule | Sequence | undefined;

export const ActionButtonWithConfirmation = ({ state, ...props }: ActionComponentProps) => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    // TODO: UX-3355: We probably need to specify permissions in the button config
    privilege: PRIVILEGES.writeCommands,
  });

  const { handleUpdateSettings, isPending: isUpdatingSettings } = useHandleUpdateSettings();

  // Store the data for the inputs inside the dialog.
  // The key here is the ID of the dropdown component.
  const [data, setData] = useState<Record<string, SettingValueKind>>({});
  const [open, setOpen] = useState(false);

  const buttonConfig = useMemo(() => {
    return state?.actionComponentConfig?.buttonComponentActionConfig;
  }, [state?.actionComponentConfig?.buttonComponentActionConfig]);

  const requiresConfirmation = useMemo(() => {
    return buttonConfig?.confirmationDialogConfig;
  }, [buttonConfig]);

  useEffect(() => {
    if (open) {
      // Initialize the data when we open the dialog.
      const id = buttonConfig?.confirmationDialogConfig?.dropdownComponentConfig?.id;
      if (id) {
        setData({
          [id as string]: undefined,
        });
      }
    }
  }, [buttonConfig, state?.stateName, open]);

  // We can only submit if all the data is defined.
  const canSubmit = data && Object.values(data).every((value) => value !== undefined);

  if (!buttonConfig || !requiresConfirmation) {
    // Should never happen, we shouldn't be in this component without a confirmation dialog config.
    return null;
  }

  const handleButtonClick = async () => {
    // Collect the actions from the dropdown option that is selected.
    let enumActions: SettingAction[] = [];
    if (buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.id) {
      const id = buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.id;
      const enumValue = data[id as string];
      if (enumValue) {
        enumActions =
          buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.options.find(
            (option: DropdownOption) => option.value === enumValue
          )?.actions?.settingActions || [];
      }
    }

    // Collect the actions from the button that opens the confirmation dialog.
    const settingActions: SettingAction[] = [
      ...(buttonConfig.actions?.settingActions || []),
      ...enumActions,
    ];

    handleUpdateSettings?.(settingActions);
  };

  // A template that sets no icon gets no icon: the critical icon belongs only to
  // actions that delete something or carry comparable impact.
  const confirmationIcon = requiresConfirmation.icon;

  const label = buttonConfig.label ?? 'Button';
  const title = requiresConfirmation.title || 'Are you sure you want to perform this action?';

  const buttonVariant = buttonConfig.variant ?? ButtonComponentActionConfigVariant.Default;

  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // The async command must finish before the dialog closes; Radix skips the
    // Action's auto-close when the click is default-prevented.
    e.preventDefault();
    await handleButtonClick();
    setOpen(false);
  };

  const disabledByConfig = buttonConfig.disabled ?? false;
  const disabled = disabledByConfig || isUpdatingSettings || disabledByPermissions;
  return (
    <div onClick={(e) => e.stopPropagation()} data-state-name={state?.stateName}>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>
          <TooltipButton
            {...props}
            className={cn('text-wrap w-full', mapLegacyButtonVariantToClassName(buttonVariant))}
            data-state-name={state?.stateName}
            disabled={disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
            tooltipContent={tooltipText}
            wrapperClassName="w-full"
          >
            {label}
          </TooltipButton>
        </AlertDialogTrigger>
        <AlertDialogContent
          data-component="ActionButtonWithConfirmation"
          className="dialog-md"
          onClick={(e) => e.stopPropagation()}
          onEscapeKeyDown={keepEscapeInDialog}
        >
          <AlertDialogTitleHeader
            icon={
              confirmationIcon ? (
                <ActionIcon
                  icon={confirmationIcon.icon}
                  size="lg"
                  variant={confirmationIcon.variant}
                />
              ) : undefined
            }
            title={title}
          />
          <AlertDialogBody>
            <Stack className="stack-md">
              {requiresConfirmation.description ? (
                <AlertDialogDescription asChild>
                  <Typography as="div" className="text-body-base">
                    {requiresConfirmation.description}
                  </Typography>
                </AlertDialogDescription>
              ) : (
                <AlertDialogDescription className="sr-only">{title}</AlertDialogDescription>
              )}
              <ConfirmationDescription
                data={data ?? {}}
                setData={setData}
                state={state!}
                requiresConfirmation={requiresConfirmation!}
              />
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogFooterButtons>
              <AlertDialogCancel asChild>
                <Button className="btn-tier3" onClick={(e) => e.stopPropagation()}>
                  {requiresConfirmation.cancelLabel || 'Cancel'}
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  className={mapLegacyButtonVariantToClassName(
                    requiresConfirmation.confirmVariant ?? ButtonComponentActionConfigVariant.Tier2
                  )}
                  disabled={!canSubmit || isUpdatingSettings}
                  onClick={handleConfirm}
                >
                  {requiresConfirmation.confirmLabel || 'Confirm'}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooterButtons>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
